import { Directive, Input, signal, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, Subject, switchMap } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

import { SelectItemsService } from '../../services/select-items.service';
import { SelectTemplatesService } from '../../services/select-templates.service';
import { SelectFilterFn } from '../../types';
import { SearchResolverDirective } from './template/search-resolver.directive';
import { SEARCH_FEATURE, SearchFeature } from './types';

@Directive({
  standalone: true,
  selector: 'rt-select2[withSearch]',
  hostDirectives: [SearchResolverDirective],
  providers: [{ provide: SEARCH_FEATURE, useExisting: SearchFeatureDirective }],
})
export class SearchFeatureDirective implements SearchFeature {
  @Input() public filterFunction: SelectFilterFn;
  @Input() public disableLocalSearch = false;
  @Input() public searchControl: FormControl<string | null> = new FormControl<string>('');

  public searchControl$ = new BehaviorSubject(this.searchControl);
  private searchQuery = signal('');
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private selectItemsService: SelectItemsService,
    private selectTemplatesService: SelectTemplatesService,
    private searchResolver: SearchResolverDirective,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.filterFunction || changes.disableLocalSearch) {
      const filter = this.disableLocalSearch ? null : this.filterFunction ?? this.defaultFilter;
      this.changeFilter(filter);
    }

    if (changes.searchControl) {
      this.searchControl$.next(this.searchControl);
    }
  }

  public ngOnInit() {
    if (!this.filterFunction && !this.disableLocalSearch) {
      this.changeFilter(this.defaultFilter);
    }

    this.searchControl$
      .pipe(
        switchMap(control => control.valueChanges.pipe(startWith(control.value))),
        debounceTime(50),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(query => {
        this.searchQuery.set(query ? query.trim().toLowerCase() : '');
      });
  }

  public ngAfterContentInit() {
    this.selectTemplatesService.headerTemplateRegister({
      id: 'search',
      index: 5,
      template: this.searchResolver.resolve().templateRef,
    });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private defaultFilter: SelectFilterFn = item =>
    item.label.toLowerCase().includes(this.searchQuery());

  private changeFilter(fn: SelectFilterFn | null) {
    if (!fn) return this.selectItemsService.filterRemove('withSearch');

    this.selectItemsService.filterRegister('withSearch', fn);
  }
}
